'use client';

import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, CreditCard, RefreshCw, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomCreditCardGeneratorContent } from '../../data/random-credit-card-generator-content';
import Link from 'next/link';

type CardType = 'visa' | 'mastercard' | 'amex' | 'discover' | 'jcb' | 'diners' | 'maestro' | 'unionpay';

interface CreditCard {
    number: string;
    type: CardType;
    expiry: string;
    cvv: string;
}

const CARD_PREFIXES = {
    visa: ['4'],
    mastercard: ['51', '52', '53', '54', '55', '2221', '2222', '2223', '2224', '2225', '2226', '2227', '2228', '2229', '223', '224', '225', '226', '227', '228', '229', '23', '24', '25', '26', '270', '271', '2720'],
    amex: ['34', '37'],
    discover: ['6011', '644', '645', '646', '647', '648', '649', '65'],
    jcb: ['3528', '3529', '353', '354', '355', '356', '357', '358'],
    diners: ['300', '301', '302', '303', '304', '305', '36', '38', '39'],
    maestro: ['5018', '5020', '5038', '5893', '6304', '6759', '6761', '6762', '6763'],
    unionpay: ['62', '88']
};

const CARD_LENGTHS = {
    visa: 16,
    mastercard: 16,
    amex: 15,
    discover: 16,
    jcb: 16,
    diners: 14,
    maestro: [12, 13, 14, 15, 16, 17, 18, 19],
    unionpay: [16, 17, 18, 19]
};

function generateLuhnCheckDigit(partialCardNumber: string): number {
    let sum = 0;
    const parity = partialCardNumber.length % 2;

    for (let i = 0; i < partialCardNumber.length; i++) {
        let digit = parseInt(partialCardNumber[i], 10);

        if (i % 2 === parity) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
    }

    return (10 - (sum % 10)) % 10;
}

function generateRandomNumber(length: number): string {
    let result = '';
    for (let i = 0; i < length; i++) {
        result += Math.floor(Math.random() * 10).toString();
    }
    return result;
}

function generateCardNumber(type: CardType): string {
    const prefixes = CARD_PREFIXES[type];
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    let cardNumber = prefix;

    // Generate remaining digits (excluding the check digit)
    const lengths = Array.isArray(CARD_LENGTHS[type])
        ? (CARD_LENGTHS[type] as number[])
        : [CARD_LENGTHS[type] as number];

    const length = lengths[Math.floor(Math.random() * lengths.length)];
    const remainingLength = length - prefix.length - 1;

    if (remainingLength > 0) {
        cardNumber += generateRandomNumber(remainingLength);
    }

    // Generate check digit
    const checkDigit = generateLuhnCheckDigit(cardNumber);
    cardNumber += checkDigit.toString();

    return cardNumber;
}

function generateExpiryDate(): string {
    const now = new Date();
    const month = String(now.getMonth() + 1 + Math.floor(Math.random() * 12)).padStart(2, '0');
    const year = String(now.getFullYear() + Math.floor(Math.random() * 5) + 1).slice(-2);
    return `${month}/${year}`;
}

function generateCVV(type: CardType): string {
    const length = type === 'amex' ? 4 : 3;
    return generateRandomNumber(length);
}

function formatCardNumber(number: string, type: CardType): string {
    if (type === 'amex') {
        return number.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
    }
    return number.replace(/(\d{4})(?=\d)/g, '$1 ');
}

function detectCardType(number: string): CardType {
    const num = number.replace(/\D/g, '');

    for (const [type, prefixes] of Object.entries(CARD_PREFIXES)) {
        const prefixArray = Array.isArray(prefixes) ? prefixes : [prefixes];
        if (prefixArray.some(prefix => num.startsWith(prefix))) {
            return type as CardType;
        }
    }

    return 'visa'; // Default to Visa if no match found
}

function generateCard(type: CardType): CreditCard {
    const number = generateCardNumber(type);
    return {
        number,
        type: detectCardType(number),
        expiry: generateExpiryDate(),
        cvv: generateCVV(type)
    };
}

export default function RandomCreditCardGenerator() {
    const [cardType, setCardType] = useState<CardType>('visa');
    const [card, setCard] = useState<CreditCard>(() => generateCard('visa'));
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const handleGenerate = useCallback(() => {
        setCard(generateCard(cardType));
    }, [cardType]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            toast({
                title: 'Copied!',
                description: 'Card number has been copied to clipboard.',
            });
            setTimeout(() => setCopied(false), 2000);
        });
    }, [toast]);

    const handleCardTypeChange = (type: string) => {
        const cardType = type as CardType;
        setCardType(cardType);
        setCard(generateCard(cardType));
    };

    const cardTypes: { value: CardType; label: string }[] = [
        { value: 'visa', label: 'Visa' },
        { value: 'mastercard', label: 'Mastercard' },
        { value: 'amex', label: 'American Express' },
        { value: 'discover', label: 'Discover' },
        { value: 'jcb', label: 'JCB' },
        { value: 'diners', label: 'Diners Club' },
        { value: 'maestro', label: 'Maestro' },
        { value: 'unionpay', label: 'UnionPay' }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <ToolPageIntro title="Random Credit Card Generator">
                Generate random credit card numbers with our free online tool. Create test credit cards for payment testing, e-commerce validation, and development.
            </ToolPageIntro>

                <div className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Card Type</CardTitle>
                            <CardDescription>Select a card type to generate</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Tabs
                                defaultValue="visa"
                                onValueChange={handleCardTypeChange}
                                value={cardType}
                                className="w-full"
                            >
                                <TabsList className="flex flex-wrap h-auto p-1">
                                    {cardTypes.map((type) => (
                                        <TabsTrigger
                                            key={type.value}
                                            value={type.value}
                                            className="flex-1 whitespace-nowrap"
                                        >
                                            {type.label}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </Tabs>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <CardTitle>Generated Card</CardTitle>
                                <Button onClick={handleGenerate} variant="outline" size="sm">
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Generate New
                                </Button>
                            </div>
                            <CardDescription>Use this test card for development and testing</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-6">
                                {/* Card Preview */}
                                <div className={cn(
                                    "relative p-6 rounded-xl text-white overflow-hidden",
                                    card.type === 'amex' ? "bg-gradient-to-br from-blue-600 to-blue-800" : "bg-gradient-to-br from-purple-600 to-blue-600"
                                )}>
                                    <div className="flex justify-between items-start mb-8">
                                        <div>
                                            <div className="text-sm opacity-80">Card Number</div>
                                            <div className="text-xl font-mono tracking-wider">
                                                {formatCardNumber(card.number, card.type)}
                                            </div>
                                        </div>
                                        <div className="text-3xl">
                                            {card.type === 'visa' && '💳'}
                                            {card.type === 'mastercard' && '💳'}
                                            {card.type === 'amex' && '💳'}
                                            {card.type === 'discover' && '💳'}
                                            {card.type === 'jcb' && '💳'}
                                            {card.type === 'diners' && '💳'}
                                            {card.type === 'maestro' && '💳'}
                                            {card.type === 'unionpay' && '💳'}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-end">
                                        <div>
                                            <div className="text-xs opacity-80">Card Holder</div>
                                            <div className="text-sm font-medium">TEST CARD</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs opacity-80">Expires</div>
                                            <div className="text-sm font-medium">{card.expiry}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Card Details */}
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-sm font-medium mb-1">Card Number</h3>
                                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                                            <code className="font-mono">{formatCardNumber(card.number, card.type)}</code>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => copyToClipboard(card.number.replace(/\D/g, ''))}
                                            >
                                                {copied ? (
                                                    <Check className="h-4 w-4 text-green-500" />
                                                ) : (
                                                    <Copy className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <h3 className="text-sm font-medium mb-1">Expiry Date</h3>
                                            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                                                <code>{card.expiry}</code>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => copyToClipboard(card.expiry)}
                                                >
                                                    {copied ? (
                                                        <Check className="h-4 w-4 text-green-500" />
                                                    ) : (
                                                        <Copy className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-medium mb-1">CVV</h3>
                                            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                                                <code>{card.cvv}</code>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => copyToClipboard(card.cvv)}
                                                >
                                                    {copied ? (
                                                        <Check className="h-4 w-4 text-green-500" />
                                                    ) : (
                                                        <Copy className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium mb-1">Card Type</h3>
                                        <div className="p-3 bg-muted/50 rounded-md">
                                            <div className="flex items-center">
                                                <CreditCard className="h-4 w-4 mr-2" />
                                                <span className="capitalize">{card.type}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                        <CardFooter className="text-xs text-muted-foreground">
                            <p>Note: These are test card numbers that follow the Luhn algorithm but are not valid for real transactions.</p>
                        </CardFooter>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomCreditCardGeneratorContent.title}
                description={randomCreditCardGeneratorContent.description}
                content={randomCreditCardGeneratorContent.content}
                faqs={randomCreditCardGeneratorContent.faqs}
                relatedTools={randomCreditCardGeneratorContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}